/**
 * SPLINE serializer. The `spline` drafting entity (Faz 7.2) exports its
 * parametric source: CV mode writes the full control-point representation
 * (degree 71, knots 42, control points 10/20/30, weights 41), fit-only
 * splines write the fit-point representation (group 11) for readers to
 * refit. Legacy `curve`/`nurbs-curve` citizens export from their display
 * tessellation (geometry fully preserved, no downgrade).
 */

import type { Entity } from '../../../../state/store';
import type { DxfWriteContext } from '../types';
import type { DxfStreamWriter } from '../DxfStreamWriter';
import { entityPrologue } from './common';

export function writeSpline(w: DxfStreamWriter, ctx: DxfWriteContext, e: Entity, owner: string): void {
    const d = e.data as Record<string, any>;
    let points = Array.isArray(d.controlPoints) ? (d.controlPoints as number[][]) : [];
    if (e.type === 'nurbs-curve' && Array.isArray(d.tessellation)) {
        points = d.tessellation as number[][];
    }
    const fitMode = e.type === 'spline' && points.length < 2 && Array.isArray(d.fitPoints);
    if (fitMode) points = d.fitPoints as number[][];
    if (points.length < 2) return;

    entityPrologue(w, ctx, e, 'SPLINE', owner);
    w.pair(100, 'AcDbSpline');
    // Flags: 1 closed | 2 rational (weights present) | 8 planar.
    const closed = !!d.closed;
    const rational = Array.isArray(d.weights) && (d.weights as number[]).some((x) => x !== 1);
    const hasKnots = e.type === 'spline' && Array.isArray(d.knots) && (d.knots as number[]).length > 0
        && Array.isArray(d.controlPoints);
    const knotCount = hasKnots ? (d.knots as number[]).length : 0;
    const fitPoints = e.type === 'spline' && Array.isArray(d.fitPoints) ? (d.fitPoints as number[][]) : [];
    w.pair(70, (closed ? 1 : 0) | (rational ? 2 : 0) | 8);
    w.pair(71, typeof d.degree === 'number' ? d.degree : 3);
    w.pair(72, knotCount);
    w.pair(73, points.length); // control-point count
    w.pair(74, fitPoints.length); // fit-point count
    if (hasKnots) (d.knots as number[]).forEach((k) => w.pair(42, k));
    if (rational) (d.weights as number[]).forEach((x) => w.pair(41, x));
    points.forEach((p) => w.pair(10, p[0] ?? 0).pair(20, p[1] ?? 0).pair(30, p[2] ?? 0));
    fitPoints.forEach((p) => w.pair(11, p[0] ?? 0).pair(21, p[1] ?? 0).pair(31, p[2] ?? 0));
}
